/**
 * 🚀 DATA CLIENT REACT HOOKS
 * 
 * React hooks that provide access to the shared data client
 * following Convex best practices for client-side integration.
 * 
 * HYBRID APPROACH:
 * - Keep useQuery for reactive data fetching (maintains auto-updates)
 * - Use data client for mutations (centralized validation & error handling)
 */

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth, useSession } from '@clerk/clerk-react';
import { api } from '@convex/_generated/api';
import type { FunctionArgs, FunctionReturnType } from 'convex/server';
import { 
  createConvexClient, 
  createDomainsClient, 
  createAuthClient,
  createAPIKeysClient,
  createCapabilitiesClient,
  createProvidersClient,
  createStatsClient,
  createUsersClient,
  type ClientConfig 
} from '@dnplace/data-client';
import { getConvexUrlOptional } from '@dnplace/config/browser';
import type { CreateDomainRequestDTO, UpdateDomainRequestDTO } from '@dnplace/types';

/**
 * Hook to get an authenticated Convex client
 * 
 * ✅ FIX: Now updates auth token when session changes (e.g., after org creation)
 */
export function useConvexClient(): ReturnType<typeof createConvexClient> {
  const { getToken } = useAuth();
  const { session } = useSession();
  const clientRef = useRef<ReturnType<typeof createConvexClient> | null>(null);
  
  // Create client once
  if (!clientRef.current) {
    const config: ClientConfig = {
      convexUrl: getConvexUrlOptional() || 'http://localhost:3210',
    };
    clientRef.current = createConvexClient(config);
  }
  
  // Update auth token whenever session changes
  useEffect(() => {
    if (!clientRef.current) return;
    
    console.log('🔄 Setting up Convex client auth token fetcher after session change');
    
    // ⚠️ CRITICAL FIX: ConvexHttpClient.setAuth() expects a STRING, not a function!
    // Only ConvexReactClient (WebSocket) accepts an async function.
    // We need to fetch the token and pass it as a string.
    async function updateAuth(): Promise<void> {
      try {
        const token = await getToken({ template: 'convex', skipCache: true });
        const debugLen = token ? token.length : 0;
        console.log('🔑 Data client fetched token length:', debugLen);
        
        if (token && clientRef.current) {
          clientRef.current.setAuth(token); // Pass the STRING token, not a function
          console.log('✅ Data client auth updated with token string');
        } else if (clientRef.current) {
          clientRef.current.clearAuth();
          console.log('🚫 Data client auth cleared (no token)');
        }
      } catch (error) {
        console.error('Failed to fetch Convex auth token:', error);
        if (clientRef.current) {
          clientRef.current.clearAuth();
        }
      }
    }
    
    updateAuth();
  }, [session, getToken]); // Re-run when session changes (e.g., after org creation)
  
  return clientRef.current;
}

/**
 * Hook to get domains client
 */
export function useDomainsClient(): ReturnType<typeof createDomainsClient> {
  const client = useConvexClient();
  
  return useMemo(() => {
    return createDomainsClient(client);
  }, [client]);
}

/**
 * Hook to get auth client
 */
export function useAuthClient(): ReturnType<typeof createAuthClient> {
  const client = useConvexClient();
  
  return useMemo(() => {
    return createAuthClient(client);
  }, [client]);
}

/**
 * Hook to get API keys client
 */
export function useAPIKeysClient(): ReturnType<typeof createAPIKeysClient> {
  const client = useConvexClient();
  
  return useMemo(() => {
    return createAPIKeysClient(client);
  }, [client]);
}

/**
 * Hook to get capabilities client
 */
export function useCapabilitiesClient(): ReturnType<typeof createCapabilitiesClient> {
  const client = useConvexClient();
  
  return useMemo(() => {
    return createCapabilitiesClient(client);
  }, [client]);
}

/**
 * Hook to get providers client
 */
export function useProvidersClient(): ReturnType<typeof createProvidersClient> {
  const client = useConvexClient();
  
  return useMemo(() => {
    return createProvidersClient(client);
  }, [client]);
}

/**
 * Hook to get stats client
 */
export function useStatsClient(): ReturnType<typeof createStatsClient> {
  const client = useConvexClient();
  
  return useMemo(() => {
    return createStatsClient(client);
  }, [client]);
}

/**
 * Hook to get users client
 */
export function useUsersClient(): ReturnType<typeof createUsersClient> {
  const client = useConvexClient();
  
  return useMemo(() => {
    return createUsersClient(client);
  }, [client]);
}

// ========================================
// 🔄 DOMAIN MUTATION HOOKS
// ========================================

interface DomainMutationsReturn {
  isLoading: boolean;
  createDomain: (dto: CreateDomainRequestDTO) => Promise<boolean>;
  updateDomain: (dto: UpdateDomainRequestDTO) => Promise<boolean>;
  deleteDomain: (domainId: string) => Promise<boolean>;
  initiateDomainVerification: (domainId: string, method: "txt" | "html" | "meta") => Promise<{ success: boolean; verificationCode?: string; message?: string }>;
  verifyDomain: (domainId: string) => Promise<{ success: boolean; message?: string; verifiedAt?: number; dnsDetails?: unknown }>;
}

/**
 * Hook for domain mutations using data client
 * Provides the same interface as the old useDomains hook but with data client backend
 */
export function useDomainMutations(): DomainMutationsReturn {
  const domainsClient = useDomainsClient();
  const [isLoading, setIsLoading] = useState(false);

  const createDomain = useCallback(async (dto: CreateDomainRequestDTO): Promise<boolean> => {
    setIsLoading(true);
    try {
      await domainsClient.createDomain(dto);
      return true;
    } catch (error) {
      console.error("Create domain error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [domainsClient]);

  const updateDomain = useCallback(async (dto: UpdateDomainRequestDTO): Promise<boolean> => {
    setIsLoading(true);
    try {
      await domainsClient.updateDomain(dto);
      return true;
    } catch (error) {
      console.error("Update domain error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [domainsClient]);

  const deleteDomain = useCallback(async (domainId: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      await domainsClient.deleteDomain(domainId);
      return true;
    } catch (error) {
      console.error("Delete domain error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [domainsClient]);

  const initiateDomainVerification = useCallback(async (domainId: string, method: "txt" | "html" | "meta"): Promise<{ success: boolean; verificationCode?: string; message?: string }> => {
    setIsLoading(true);
    try {
      const result = await domainsClient.initiateDomainVerification(domainId, method);
      return result;
    } catch (error) {
      console.error("Initiate domain verification error:", error);
      return { success: false, message: error instanceof Error ? error.message : "Failed to initiate domain verification" };
    } finally {
      setIsLoading(false);
    }
  }, [domainsClient]);

  const verifyDomain = useCallback(async (domainId: string): Promise<{ success: boolean; message?: string; verifiedAt?: number; dnsDetails?: unknown }> => {
    setIsLoading(true);
    try {
      const result = await domainsClient.verifyDomain(domainId);
      return result;
    } catch (error) {
      console.error("Verify domain error:", error);
      return { success: false, message: error instanceof Error ? error.message : "Failed to verify domain" };
    } finally {
      setIsLoading(false);
    }
  }, [domainsClient]);

  return {
    isLoading,
    createDomain,
    updateDomain,
    deleteDomain,
    initiateDomainVerification,
    verifyDomain,
  };
}

// ========================================
// 🏷️ BULK OPERATIONS HOOKS
// ========================================

interface BulkDomainOperationsReturn {
  isLoading: boolean;
  bulkAddTags: (domainIds: string[], tags: string[]) => Promise<{ success: boolean; updated: number; message?: string }>;
  bulkRemoveTags: (domainIds: string[], tags: string[]) => Promise<{ success: boolean; updated: number; message?: string }>;
}

/**
 * Hook for bulk domain operations using data client
 */
export function useBulkDomainOperations(): BulkDomainOperationsReturn {
  const domainsClient = useDomainsClient();
  const [isLoading, setIsLoading] = useState(false);

  const bulkAddTags = useCallback(async (domainIds: string[], tags: string[]) => {
    setIsLoading(true);
    try {
      const result = await domainsClient.bulkAddTags(domainIds, tags);
      return result;
    } catch (error) {
      console.error("Bulk add tags error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [domainsClient]);

  const bulkRemoveTags = useCallback(async (domainIds: string[], tags: string[]) => {
    setIsLoading(true);
    try {
      const result = await domainsClient.bulkRemoveTags(domainIds, tags);
      return result;
    } catch (error) {
      console.error("Bulk remove tags error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [domainsClient]);

  return {
    isLoading,
    bulkAddTags,
    bulkRemoveTags,
  };
}

// ========================================
// 🔑 API KEY MUTATION HOOKS
// ========================================

interface APIKeyMutationsReturn {
  isLoading: boolean;
  createAPIKey: (data: FunctionArgs<typeof api.apiKeys.createAPIKey>) => Promise<FunctionReturnType<typeof api.apiKeys.createAPIKey>>;
  updateAPIKey: (data: FunctionArgs<typeof api.apiKeys.updateAPIKey>) => Promise<FunctionReturnType<typeof api.apiKeys.updateAPIKey>>;
  revokeAPIKey: (keyId: string) => Promise<FunctionReturnType<typeof api.apiKeys.revokeAPIKey>>;
  enableAPIKey: (keyId: string) => Promise<FunctionReturnType<typeof api.apiKeys.updateAPIKey>>;
  disableAPIKey: (keyId: string) => Promise<FunctionReturnType<typeof api.apiKeys.updateAPIKey>>;
  rotateAPIKeySecret: (keyId: string) => Promise<FunctionReturnType<typeof api.apiKeys.rotateAPIKeySecret>>;
}

/**
 * Hook for API key mutations using data client
 */
export function useAPIKeyMutations(): APIKeyMutationsReturn {
  const apiKeysClient = useAPIKeysClient();
  const [isLoading, setIsLoading] = useState(false);

  const createAPIKey = useCallback(async (
    keyData: FunctionArgs<typeof api.apiKeys.createAPIKey>
  ): Promise<Awaited<ReturnType<typeof apiKeysClient.createAPIKey>>> => {
    setIsLoading(true);
    try {
      const result = await apiKeysClient.createAPIKey(keyData);
      return result;
    } catch (error) {
      console.error("Create API key error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiKeysClient]);

  const updateAPIKey = useCallback(async (
    keyData: FunctionArgs<typeof api.apiKeys.updateAPIKey>
  ): Promise<Awaited<ReturnType<typeof apiKeysClient.updateAPIKey>>> => {
    setIsLoading(true);
    try {
      const result = await apiKeysClient.updateAPIKey(keyData);
      return result;
    } catch (error) {
      console.error("Update API key error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiKeysClient]);

  const revokeAPIKey = useCallback(async (
    keyId: string
  ): Promise<Awaited<ReturnType<typeof apiKeysClient.revokeAPIKey>>> => {
    setIsLoading(true);
    try {
      const result = await apiKeysClient.revokeAPIKey(keyId);
      return result;
    } catch (error) {
      console.error("Revoke API key error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiKeysClient]);

  const enableAPIKey = useCallback(async (
    keyId: string
  ): Promise<Awaited<ReturnType<typeof apiKeysClient.enableAPIKey>>> => {
    setIsLoading(true);
    try {
      const result = await apiKeysClient.enableAPIKey(keyId);
      return result;
    } catch (error) {
      console.error("Enable API key error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiKeysClient]);

  const disableAPIKey = useCallback(async (
    keyId: string
  ): Promise<Awaited<ReturnType<typeof apiKeysClient.disableAPIKey>>> => {
    setIsLoading(true);
    try {
      const result = await apiKeysClient.disableAPIKey(keyId);
      return result;
    } catch (error) {
      console.error("Disable API key error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiKeysClient]);

  const rotateAPIKeySecret = useCallback(async (
    keyId: string
  ): Promise<Awaited<ReturnType<typeof apiKeysClient.rotateAPIKeySecret>>> => {
    setIsLoading(true);
    try {
      const result = await apiKeysClient.rotateAPIKeySecret(keyId);
      return result;
    } catch (error) {
      console.error("Rotate API key secret error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiKeysClient]);

  return {
    isLoading,
    createAPIKey,
    updateAPIKey,
    revokeAPIKey,
    enableAPIKey,
    disableAPIKey,
    rotateAPIKeySecret,
  };
}
